const GENERIC = "Η απάντηση δεν ολοκληρώθηκε. Δοκιμάστε ξανά.";

export class AIRequestError extends Error {
  constructor(message, code = "request_failed", retryable = true) {
    super(message);
    this.name = "AIRequestError";
    this.code = code;
    this.retryable = retryable;
  }
}

export function aiErrorMessage(error) {
  return error instanceof AIRequestError
    ? error.message
    : "Δεν ήταν δυνατή η σύνδεση με την υπηρεσία AI. Ελέγξτε τη σύνδεσή σας και δοκιμάστε ξανά.";
}

function responseError(detail, status) {
  if (detail && typeof detail === "object" && !Array.isArray(detail) && typeof detail.message === "string") {
    return new AIRequestError(detail.message, detail.code, detail.retryable !== false);
  }
  return new AIRequestError(status === 422
    ? "Ελέγξτε τα πεδία σας και δοκιμάστε ξανά."
    : "Η υπηρεσία AI δεν είναι διαθέσιμη αυτή τη στιγμή. Δοκιμάστε ξανά σε λίγο.");
}

// Parses UTF-8 SSE frames across arbitrary network chunks. Only [DONE] means success.
export async function readSSE(url, body, onDelta, { signal, timeoutMs = 120000 } = {}) {
  const controller = new AbortController();
  let reader;
  let timedOut = false;
  let receivedText = false;
  const abort = () => controller.abort();
  if (signal?.aborted) abort();
  signal?.addEventListener("abort", abort, { once: true });
  const timer = setTimeout(() => { timedOut = true; controller.abort(); }, timeoutMs);
  try {
    const res = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "text/event-stream" },
      body: JSON.stringify(body),
      signal: controller.signal,
    });
    if (!res.ok) {
      let json;
      try { json = await res.json(); } catch { /* A proxy may return an HTML error. */ }
      throw responseError(json?.detail, res.status);
    }
    if (!res.body || !res.headers.get("content-type")?.includes("text/event-stream")) {
      throw new AIRequestError(GENERIC, "invalid_stream");
    }
    reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";
    const consume = (frame) => {
      const data = frame.split("\n").filter((line) => line.startsWith("data:"))
        .map((line) => line.slice(5).replace(/^ /, "")).join("\n");
      if (!data) return false; // comments/keepalive frames
      if (data === "[DONE]") {
        if (!receivedText) throw new AIRequestError(GENERIC, "empty_stream");
        return true;
      }
      let event;
      try { event = JSON.parse(data); } catch { throw new AIRequestError(GENERIC, "invalid_stream"); }
      if (!event || typeof event !== "object") throw new AIRequestError(GENERIC, "invalid_stream");
      if (event.error) {
        if (typeof event.error === "string") throw new AIRequestError(event.error);
        throw responseError(event.error);
      }
      if (typeof event.delta === "string" && event.delta) {
        receivedText = true;
        onDelta(event.delta);
      }
      return false;
    };
    for (;;) {
      const { done, value } = await reader.read();
      buffer += done ? decoder.decode() : decoder.decode(value, { stream: true });
      buffer = buffer.replace(/\r\n/g, "\n");
      let boundary;
      while ((boundary = buffer.indexOf("\n\n")) !== -1) {
        const frame = buffer.slice(0, boundary);
        buffer = buffer.slice(boundary + 2);
        if (consume(frame)) return;
      }
      if (done) {
        if (buffer.trim() && consume(buffer)) return;
        throw new AIRequestError("Η σύνδεση διακόπηκε πριν ολοκληρωθεί η απάντηση. Δοκιμάστε ξανά.", "interrupted_stream");
      }
    }
  } catch (error) {
    if (timedOut) throw new AIRequestError("Η υπηρεσία AI αργεί να απαντήσει. Δοκιμάστε ξανά.", "timeout");
    if (signal?.aborted) throw error;
    if (error instanceof AIRequestError) throw error;
    throw new AIRequestError(aiErrorMessage(error), "network_error");
  } finally {
    clearTimeout(timer);
    signal?.removeEventListener("abort", abort);
    if (reader) {
      await reader.cancel().catch(() => {});
      reader.releaseLock();
    }
  }
}
